import classNames from "classnames"
import React from "react"

import BulletListCell from "./BulletListCell"
import ButtonCell from "./ButtonCell"
import IconCell from "./IconCell"
import ListCell from "./ListCell"
import PriceCell from "./PriceCell"
import {
  extractFromTag,
  getIconFillClass,
  getThemeColor,
  getCheckmarkStrokeColor,
  getDashStrokeColor,
  getCellBackgroundClass,
  getTooltipBackgroundColor,
  parseContent,
  extractPriceData,
  parseButtonData,
  getCellRoundingClasses,
  matchCellPatterns,
} from "./utils"
import { Color } from "../../../../constants/V2/color"
import CircleCheckIcon from "../../../elements/V2/Icons/CircleCheckIcon"
import CircleCrossIcon from "../../../elements/V2/Icons/CircleCrossIcon"
import Logo from "../../../elements/V2/Icons/YocoLogoIcon"
import Tooltip from "../../../elements/V2/Tooltip"
import Typography from "../../../elements/V2/Typography"

import CircleDashIcon from "@components/elements/V2/Icons/CircleDashIcon"
import { TableRegex } from "@constants/tables"
import { useGetProductBySlug } from "@hooks/V2/storyblok/useProduct"
import { checkPromotion } from "@utils/shop"
import { getAccentColor } from "@utils/V2/color"

const CELL_CLASSES = "text-center flex items-center px-16 py-20 rounded-10"
export const CELL_TEXT_CLASSSES =
  "[display:-webkit-box] [-webkit-box-orient:vertical] text-start"

type CellDataProps = {
  rowIndex: number
  columnIndex: number
  displayIndex: number
  data: string[][]
  rowHeadings: string[]
  tableTheme: "light" | "dark"
  backgroundColor: Color.Black | Color.Charcoal | Color.White | Color.MintGreen
  maxMobileColumns: number
  maxDesktopColumns: number
  titleRowIndices: React.MutableRefObject<number[]>
}

const CellData = ({
  rowIndex,
  columnIndex,
  displayIndex,
  data,
  rowHeadings,
  tableTheme,
  backgroundColor,
  maxMobileColumns,
  maxDesktopColumns,
  titleRowIndices,
}: CellDataProps) => {
  const getProductBySlug = useGetProductBySlug()
  const cellData = data[rowIndex][columnIndex]
  const matches = matchCellPatterns(cellData)
  let price = 0

  const bulletPoints = parseContent(matches.isBullet, 8)
  const list = parseContent(matches.isList, 6)
  let priceSubScript
  let priceSideScript
  let promotionStartDate
  let promotionEndDate
  let promotionSavings = 0

  if (
    matches.isPriceFromStoryblokSlug ||
    matches.isDetailedPriceFromStoryblokSlug
  ) {
    const slug =
      matches.isDetailedPriceFromStoryblokSlug?.[1] ||
      matches.isPriceFromStoryblokSlug?.[1] ||
      ""

    const item = getProductBySlug(slug)

    if (item) {
      price = Number(item.price)
      if (matches.isDetailedPriceFromStoryblokSlug) {
        const priceData = extractPriceData(item)
        priceSubScript = priceData.priceSubScript
        priceSideScript = priceData.priceSideScript
        promotionSavings = priceData.promotionSavings
        promotionStartDate = priceData.promotionStartDate
        promotionEndDate = priceData.promotionEndDate
      }
    }
  }
  const showPromotion = checkPromotion(promotionStartDate, promotionEndDate)
  const { buttonLabel, buttonLink, secondButtonLabel, secondButtonLink } =
    parseButtonData(matches.isButton ? cellData.match(TableRegex.BUTTON) : null)

  return (
    <div
      key={`column-${columnIndex}-row-${rowIndex}`}
      className={classNames(
        CELL_CLASSES,
        "flex !items-start",
        {
          "hidden sm:flex":
            displayIndex >= maxMobileColumns &&
            displayIndex < maxDesktopColumns,
          hidden: displayIndex >= maxDesktopColumns,
        },
        matches.isCheckIcon || matches.isCrossIcon
          ? "!pr-auto w-full flex-row-reverse !justify-end gap-8 sm:gap-32"
          : "flex-col",
        getCellRoundingClasses(displayIndex, maxDesktopColumns),
        {
          "mt-16 md:mt-24": titleRowIndices.current.find(
            (titleRowIndex) => titleRowIndex === rowIndex
          ),
          "justify-center":
            matches.isPriceFromStoryblokSlug ||
            matches.isDetailedPriceFromStoryblokSlug ||
            matches.isPrice,
        },
        getCellBackgroundClass(
          rowIndex,
          data.length,
          tableTheme,
          backgroundColor
        ),
        { "!py-0": matches.isButton }
      )}
    >
      <Tooltip
        text={
          rowHeadings[columnIndex].match(TableRegex.TITLE)
            ? extractFromTag(rowHeadings[columnIndex], "title")
            : rowHeadings[columnIndex]
        }
        backgroundColor={getTooltipBackgroundColor(tableTheme, backgroundColor)}
        showTooltipCondition="hoverContentEllipsed"
      >
        <Typography
          font="grotesk"
          weight="book"
          color={getAccentColor(
            tableTheme === "light" ? Color.White : Color.Charcoal
          )}
          size="body-sm-fixed"
          text={
            rowHeadings[rowIndex].match(TableRegex.TITLE)
              ? ""
              : rowHeadings[rowIndex]
          }
          className={classNames(
            {
              "!hidden":
                matches.isPriceFromStoryblokSlug ||
                matches.isDetailedPriceFromStoryblokSlug ||
                matches.isPrice,
              "pb-10": !(matches.isCheckIcon || matches.isCrossIcon),
              "mt-4": matches.isCheckIcon || matches.isCrossIcon,
            },
            CELL_TEXT_CLASSSES,
            "text-left opacity-70"
          )}
        />
      </Tooltip>
      {matches.isCrossIcon ? (
        <IconCell
          icon={
            <CircleCrossIcon
              className={classNames(
                "shrink-0",
                tableTheme === "light" ? "stroke-charcoal" : "stroke-white"
              )}
            />
          }
          text={matches.isCrossIcon?.[1]}
          theme={tableTheme}
        />
      ) : matches.isCheckIcon ? (
        <IconCell
          icon={
            <CircleCheckIcon
              width={29}
              height={29}
              showBorder={false}
              opacity={1}
              className={classNames(
                "shrink-0",
                getIconFillClass(matches.isCheckIcon?.[1])
              )}
              checkmarkClassName={getCheckmarkStrokeColor(
                matches.isCheckIcon?.[1]
              )}
            />
          }
          text={matches.isCheckIcon?.[2]}
          theme={tableTheme}
        />
      ) : matches.isCheckIconBlackStroke ? (
        <IconCell
          icon={
            <CircleCheckIcon
              width={29}
              height={29}
              showBorder={false}
              opacity={1}
              className={classNames(
                "shrink-0",
                getIconFillClass(matches.isCheckIconBlackStroke?.[1])
              )}
              checkmarkClassName={
                matches.isCheckIconBlackStroke?.[1] === "white"
                  ? "stroke-black"
                  : "stroke-white"
              }
            />
          }
          text={matches.isCheckIconBlackStroke?.[2]}
          theme={tableTheme}
        />
      ) : matches.isDashIcon ? (
        <div className="flex items-center">
          <CircleDashIcon
            width={29}
            height={29}
            showBorder={false}
            opacity={1}
            className={getIconFillClass(matches.isDashIcon?.[1])}
            dashClassName={getDashStrokeColor(matches.isDashIcon?.[1])}
          />
        </div>
      ) : matches.isHeader ? (
        <Typography
          text={extractFromTag(cellData, "header")}
          size="body-lg"
          font="grotesk"
          weight="medium"
          color={getAccentColor(
            tableTheme === "light" ? Color.White : Color.Charcoal
          )}
        />
      ) : cellData.match(/:yocoLogo:/) ? (
        <Logo
          className={classNames(
            "max-h-32",
            tableTheme === "light" ? "fill-black" : "fill-white"
          )}
          alt="Yoco Logo"
        />
      ) : cellData.match(/:crossIcon:/) ? (
        <CircleCrossIcon
          className={
            tableTheme === "light" ? "stroke-charcoal" : "stroke-white"
          }
        />
      ) : matches.isList && list ? (
        <ListCell items={list} theme={tableTheme} />
      ) : matches.isBullet && bulletPoints ? (
        <BulletListCell items={bulletPoints} theme={tableTheme} />
      ) : matches.isPriceFromStoryblokSlug ? (
        <PriceCell price={price} theme={tableTheme} />
      ) : matches.isDetailedPriceFromStoryblokSlug ? (
        <PriceCell
          price={price}
          theme={tableTheme}
          detailed
          priceSubScript={priceSubScript}
          priceSideScript={priceSideScript}
          promotionSavings={promotionSavings}
          showPromotion={showPromotion}
          topText={matches.isDetailedPriceFromStoryblokSlug?.[2]}
        />
      ) : matches.isButton && buttonLabel && buttonLink ? (
        <ButtonCell
          buttonLabel={buttonLabel}
          buttonLink={buttonLink}
          secondButtonLabel={secondButtonLabel}
          secondButtonLink={secondButtonLink}
          columnData={data[0][columnIndex]}
          theme={tableTheme}
        />
      ) : (
        !matches.isProduct && (
          <>
            <Tooltip
              text={
                matches.isPrice ? extractFromTag(cellData, "price") : cellData
              }
              backgroundColor={getThemeColor(tableTheme)}
              showTooltipCondition="hoverContentEllipsed"
            >
              <Typography
                font="grotesk"
                weight={matches.isPrice || rowIndex === 0 ? "medium" : "book"}
                color={getThemeColor(tableTheme)}
                size={
                  matches.isPrice
                    ? "lead-lg"
                    : rowIndex === 0
                      ? "body-lg"
                      : "body-md"
                }
                text={
                  matches.isPrice ? extractFromTag(cellData, "price") : cellData
                }
                className={CELL_TEXT_CLASSSES}
              />
            </Tooltip>
          </>
        )
      )}
    </div>
  )
}

export default CellData
